"use client";
/* eslint-disable no-unused-vars */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Cart } from "@/models/cart/cart";
import { RetailCart } from "@/models/cart/retailCart";
import { RestaurantCart } from "@/models/cart/restaurantCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import { CartType } from "@/constants/enum";
import { LocalStorageKey } from "@/utils/storageKeys";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { useMerchant } from "@/app/providers/merchantProvider";
import useLocalStorage from "@/utils/useLocalStorage";

interface CartContextType {
  cart: Cart | null;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  const { merchant } = useMerchant();
  const [cartType, setCartType] = useState<CartType>(CartType.RETAIL_CART);

  // Determine cart type based on merchant and storage content (for Hybrid)
  useEffect(() => {
    const determineCartType = () => {
      if (merchant.isRestaurant) {
        setCartType(CartType.RESTAURANT_CART);
        return;
      }

      // Only service cart
      if (
        merchant.isService &&
        !merchant.isHybrid &&
        !merchant.hasOnlyRetailItems
      ) {
        setCartType(CartType.SERVICE_CART);
        return;
      }

      // Hybrid or Retail or Service with Retail items
      if (
        merchant.isHybrid ||
        (merchant.isService && merchant.hasOnlyRetailItems) ||
        merchant.isRetail
      ) {
        // Check if Service Cart has items (Priority logic from NavigationBar)
        const serviceItems = getFromLocalStorage(
          LocalStorageKey.SERVICE_CART,
          []
        );
        const retailItems = getFromLocalStorage(
          LocalStorageKey.RETAIL_CART,
          []
        );
        const giftCard = getFromLocalStorage(LocalStorageKey.GIFT_CARD, null);

        if (
          (!serviceItems || serviceItems.length === 0) &&
          (!retailItems || retailItems.length === 0) &&
          !giftCard
        ) {
          return;
        }

        // Retail takes priority over service for hybrid merchants,
        // matching the drawer priority in CartDrawerContext.
        if (retailItems && retailItems.length > 0) {
          setCartType(CartType.RETAIL_CART);
        } else if (serviceItems && serviceItems.length > 0) {
          setCartType(CartType.SERVICE_CART);
        } else {
          setCartType(CartType.RETAIL_CART);
        }
      }
    };

    determineCartType();

    // Listen to storage events to switch type dynamically if needed
    const handleStorageChange = () => {
      determineCartType();
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [merchant]);

  const storageKey = useMemo(() => {
    switch (cartType) {
      case CartType.RETAIL_CART:
        return LocalStorageKey.RETAIL_CART;
      case CartType.RESTAURANT_CART:
        return LocalStorageKey.RESTAURANT_CART;
      case CartType.SERVICE_CART:
        return LocalStorageKey.SERVICE_CART;
      default:
        return LocalStorageKey.RETAIL_CART;
    }
  }, [cartType]);

  // Use useLocalStorage to manage the serialized data
  const [cartData] = useLocalStorage<any[]>(storageKey, []);

  // Helper to instantiate the correct cart class
  const createCartInstance = useCallback(
    (items: any[]) => {
      switch (cartType) {
        case CartType.RETAIL_CART:
          return new RetailCart({ items });
        case CartType.RESTAURANT_CART:
          return new RestaurantCart({ items });
        case CartType.SERVICE_CART:
          return new ServiceCart({ items });
        default:
          return new RetailCart({ items });
      }
    },
    [cartType]
  );

  // Deserialize data into class instance
  const cart = useMemo(() => {
    return createCartInstance(cartData);
  }, [cartData, createCartInstance]);

  const value: CartContextType = useMemo(() => ({ cart }), [cart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
